import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useClient } from '../../hooks/useClient';
import { User } from '../../types/user';
import { ReservationType } from '../../types/reservationType';
import storage from '../../storage/storage';

const RESERVATION_FILTERS_KEY_PREFIX = 'reservation-filters-';

export const useReservationFilters = ({
  userId,
  organizationId,
  userLoading,
  reservationTypesLoading,
  allReservationTypes,
}: {
  userId: string | undefined;
  organizationId: string | undefined;
  userLoading: boolean;
  reservationTypesLoading: boolean;
  allReservationTypes: ReservationType[] | undefined;
}) => {
  const [selectedBranchIds, setSelectedBranchIds] = useState<string[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<User[]>([]);
  const [selectedReservationTypes, setSelectedReservationTypes] = useState<ReservationType[]>([]);
  // The device storage keeps only the employee ids; the employees come from the organization's user list.
  const [savedEmployeeIds, setSavedEmployeeIds] = useState<string[]>([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const { get } = useClient();
  // Same key and fetcher as CompactEmployeeSelect, so both share one request. Without saved employees the key is null.
  const { data: employees, isLoading: employeesLoading } = useSWR(
    savedEmployeeIds.length > 0 && organizationId ? `/organizations/${organizationId}/users` : null,
    get<User[]>,
  );

  useEffect(() => {
    if (savedEmployeeIds.length === 0 || !organizationId || employeesLoading) return;
    setSelectedEmployees((employees ?? []).filter((employee) => savedEmployeeIds.includes(employee.id)));
    setSavedEmployeeIds([]);
  }, [savedEmployeeIds, organizationId, employees, employeesLoading]);

  // Actualizar clearFilters para limpiar arrays
  const clearFilters = useCallback(() => {
    setSelectedBranchIds([]);
    setSelectedEmployees([]);
    setSelectedReservationTypes([]);
  }, []);

  // Actualizar la lógica para verificar si hay filtros aplicados
  const hasAppliedFilters = useMemo(
    () => selectedBranchIds.length > 0 || selectedEmployees.length > 0 || selectedReservationTypes.length > 0,
    [selectedBranchIds, selectedEmployees, selectedReservationTypes],
  );

  // Efecto para cargar los filtros guardados cuando el componente se monta
  useEffect(() => {
    const loadSavedFilters = async () => {
      if (!userId || reservationTypesLoading) return;

      try {
        const key = `${RESERVATION_FILTERS_KEY_PREFIX}${userId}`;
        const savedFiltersJson = await storage.getItem(key);

        if (savedFiltersJson) {
          const savedFilters = JSON.parse(savedFiltersJson);

          // Cargar IDs de sucursales
          if (savedFilters.branchIds && Array.isArray(savedFilters.branchIds)) {
            setSelectedBranchIds(savedFilters.branchIds);
          }

          // Cargar empleados (necesitamos recuperar los objetos completos)
          if (savedFilters.employeeIds && Array.isArray(savedFilters.employeeIds)) {
            setSavedEmployeeIds(savedFilters.employeeIds);
          }

          // Cargar tipos de reserva
          if (savedFilters.reservationTypeIds && Array.isArray(savedFilters.reservationTypeIds) && allReservationTypes) {
            const types = savedFilters.reservationTypeIds
              .map((id: string) => allReservationTypes.find((type) => type.id === id))
              .filter(Boolean) as ReservationType[];

            if (types.length > 0) {
              setSelectedReservationTypes(types);
            }
          }
        }
      } catch (error) {
        console.error('Error al cargar los filtros de reservas:', error);
      } finally {
        setFiltersLoaded(true);
      }
    };

    loadSavedFilters();
  }, [userId, reservationTypesLoading, allReservationTypes]);

  // Efecto para guardar los filtros cuando cambian
  useEffect(() => {
    const saveFilters = async () => {
      if (!userId) return;

      try {
        const filtersToSave = {
          branchIds: selectedBranchIds,
          employeeIds: selectedEmployees.map((emp) => emp.id),
          reservationTypeIds: selectedReservationTypes.map((type) => type.id),
        };

        const key = `${RESERVATION_FILTERS_KEY_PREFIX}${userId}`;
        await storage.setItem(key, JSON.stringify(filtersToSave));
      } catch (error) {
        console.error('Error al guardar los filtros de reservas:', error);
      }
    };

    // Save only when the needed data is loaded and the saved filters are restored
    if (!userLoading && !reservationTypesLoading && filtersLoaded && savedEmployeeIds.length === 0) {
      saveFilters();
    }
  }, [
    userId,
    selectedBranchIds,
    selectedEmployees,
    selectedReservationTypes,
    userLoading,
    reservationTypesLoading,
    filtersLoaded,
    savedEmployeeIds,
  ]);

  return {
    selectedBranchIds,
    setSelectedBranchIds,
    selectedEmployees,
    setSelectedEmployees,
    selectedReservationTypes,
    setSelectedReservationTypes,
    clearFilters,
    hasAppliedFilters,
  };
};
